/**
 * DamageSystem - Handles damage detection and line-of-sight calculations
 */
import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { BasicFart } from '../entities/BasicFart';
import { LingeringFart } from '../entities/farts/LingeringFart';
import { GhostFart } from '../entities/farts/GhostFart';
import { ExplosiveFart } from '../entities/farts/ExplosiveFart';
import { SBDFart } from '../entities/farts/SBDFart';
import { FartType } from '../entities/farts/FartTypes';
import { TileType } from './MazeGenerator';
import { TILE_SIZE } from '../config/game.config';
import { networkManager } from '../network/NetworkManager';
import { StatsTracker } from './StatsTracker';
import { DeathReplaySystem } from './DeathReplaySystem';
import { MultiplayerSync } from './MultiplayerSync';

export class DamageSystem {
    private scene: Phaser.Scene;
    private player: Player;
    private farts: Phaser.Physics.Arcade.Group;
    private mazeData: TileType[][];
    private statsTracker: StatsTracker;
    private deathReplaySystem: DeathReplaySystem;
    private multiplayerSync?: MultiplayerSync;
    private isMultiplayer: boolean;

    constructor(
        scene: Phaser.Scene,
        player: Player,
        farts: Phaser.Physics.Arcade.Group,
        mazeData: TileType[][],
        statsTracker: StatsTracker,
        deathReplaySystem: DeathReplaySystem,
        isMultiplayer: boolean
    ) {
        this.scene = scene;
        this.player = player;
        this.farts = farts;
        this.mazeData = mazeData;
        this.statsTracker = statsTracker;
        this.deathReplaySystem = deathReplaySystem;
        this.isMultiplayer = isMultiplayer;
    }

    /**
     * Set multiplayer sync for broadcasting position after damage
     */
    setMultiplayerSync(sync: MultiplayerSync): void {
        this.multiplayerSync = sync;
    }

    /**
     * Set up physics overlap detection for damage
     */
    setupPhysicsOverlap(): void {
        this.scene.physics.add.overlap(this.player, this.farts, (obj1, obj2) => {
            const fart = obj2 as Phaser.Physics.Arcade.Sprite;

            // Ignore all damage during propulsion dash (invincibility frames)
            if ((this.player as any)._propulsionActive) return;

            // Check if this is a lingering fart
            if (fart.getData('isLingering')) {
                this.handleLingeringFartOverlap(fart as LingeringFart);
                return;
            }

            // Check if this is a ghost fart
            if (fart.getData('isGhost')) {
                this.handleGhostFartOverlap(fart as unknown as GhostFart);
                return;
            }

            // Check if this is an explosive fart
            if (fart.getData('isExplosive')) {
                this.handleExplosiveFartOverlap(fart as unknown as ExplosiveFart);
                return;
            }

            // Check if this is an SBD fart
            if (fart.getData('isSBD')) {
                this.handleSBDFartOverlap(fart as unknown as SBDFart);
                return;
            }

            // Standard fart handling
            const basicFart = fart as BasicFart;

            // Don't hit yourself
            if (basicFart.getData('ownerId') === networkManager.playerId) return;

            // Check line-of-sight - standard farts shouldn't damage through walls
            // Charged farts (with chargePercent > 0.5) can pierce walls
            const chargePercent = basicFart.getData('chargePercent') ?? 0;
            if (chargePercent < 0.5 && !this.hasLineOfSight(basicFart.x, basicFart.y, this.player.x, this.player.y)) {
                return; // Wall blocks the damage
            }

            this.applyDamage(basicFart);
            basicFart.destroy();
        });
    }

    /**
     * Handle overlap with a ghost fart (destroys on hit, has owner immunity timer)
     */
    private handleGhostFartOverlap(fart: GhostFart): void {
        if (this.player.isDead() || this.player.isStunned) return;

        // Check owner immunity (first 2 seconds)
        if (!fart.canDamagePlayer(networkManager.playerId)) return;

        // Apply damage
        const damage = fart.getDamage();
        const stunDuration = fart.getStunDuration();
        const ownerId = fart.getOwnerId();

        this.player.takeDamage(damage);
        this.player.stun(stunDuration);

        // Record stats
        if (ownerId) {
            this.statsTracker.recordDamage(ownerId, damage);
            if (this.player.health <= 0) {
                this.statsTracker.recordElimination(networkManager.playerId, ownerId);
                this.deathReplaySystem.captureKillingBlow(
                    this.player.x, this.player.y,
                    fart.x, fart.y
                );
            }
        }

        // Broadcast position after damage
        if (this.isMultiplayer && this.multiplayerSync) {
            this.multiplayerSync.broadcastPosition();
        }

        // Ghost farts destroy on impact
        fart.destroy();
    }

    /**
     * Handle overlap with an explosive fart (massive blast, knockback, self-damage)
     */
    private handleExplosiveFartOverlap(fart: ExplosiveFart): void {
        if (this.player.isDead() || this.player.isStunned) return;

        // Only deal damage during explosion
        if (!fart.isExploding()) return;

        // Check if player can be damaged (includes self!)
        if (!fart.canDamagePlayer(networkManager.playerId)) return;

        // Mark player as hit
        fart.markPlayerHit(networkManager.playerId);

        // Apply distance-based damage (closer = more damage)
        const damage = fart.getDamageAtDistance(this.player.x, this.player.y);
        const stunDuration = fart.getStunDuration();
        const ownerId = fart.getOwnerId();

        this.player.takeDamage(damage);
        this.player.stun(stunDuration);

        // Apply knockback
        const knockback = fart.getKnockbackForce(this.player.x, this.player.y);
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(knockback.x, knockback.y);

        // Record stats
        if (ownerId) {
            this.statsTracker.recordDamage(ownerId, damage);
            if (this.player.health <= 0) {
                this.statsTracker.recordElimination(networkManager.playerId, ownerId);
                this.deathReplaySystem.captureKillingBlow(
                    this.player.x, this.player.y,
                    fart.x, fart.y
                );
            }
        }

        // Broadcast position after damage
        if (this.isMultiplayer && this.multiplayerSync) {
            this.multiplayerSync.broadcastPosition();
        }

        // Note: Explosive does NOT destroy on hit - it handles its own cleanup
    }

    /**
     * Handle overlap with an SBD fart (high damage, destroys on hit)
     */
    private handleSBDFartOverlap(fart: SBDFart): void {
        if (this.player.isDead() || this.player.isStunned) return;

        // Check if can damage this player (cannot damage owner)
        if (!fart.canDamagePlayer(networkManager.playerId)) return;

        // Apply damage
        const damage = fart.getDamage();
        const stunDuration = fart.getStunDuration();
        const ownerId = fart.getOwnerId();

        this.player.takeDamage(damage);
        this.player.stun(stunDuration);

        // Record stats
        if (ownerId) {
            this.statsTracker.recordDamage(ownerId, damage);
            if (this.player.health <= 0) {
                this.statsTracker.recordElimination(networkManager.playerId, ownerId);
                this.deathReplaySystem.captureKillingBlow(
                    this.player.x, this.player.y,
                    fart.x, fart.y
                );
            }
        }

        // Broadcast position after damage
        if (this.isMultiplayer && this.multiplayerSync) {
            this.multiplayerSync.broadcastPosition();
        }

        // SBD destroys on hit
        fart.hitPlayer();
    }

    /**
     * Handle overlap with a lingering fart (tick damage, doesn't destroy)
     */
    private handleLingeringFartOverlap(fart: LingeringFart): void {
        if (this.player.isDead() || this.player.isStunned) return;

        // Lingering farts don't deal damage during dissipation
        if (!fart.canDealDamage()) return;

        // Check if this player can be damaged this tick
        if (!fart.canDamagePlayer(networkManager.playerId)) return;

        // Mark player as hit this tick
        fart.markPlayerHit(networkManager.playerId);

        // Apply tick damage
        const damage = fart.getDamage();
        const ownerId = fart.getOwnerId();

        this.player.takeDamage(damage);
        this.player.stun(fart.getStunDuration());

        // Record damage
        if (ownerId) {
            this.statsTracker.recordDamage(ownerId, damage);
            if (this.player.health <= 0) {
                this.statsTracker.recordElimination(networkManager.playerId, ownerId);
                this.deathReplaySystem.captureKillingBlow(
                    this.player.x, this.player.y,
                    fart.x, fart.y
                );
            }
        }

        if (this.isMultiplayer && this.multiplayerSync) {
            this.multiplayerSync.broadcastPosition();
        }
    }

    /**
     * Distance-based damage check as backup to physics overlap
     * Ensures symmetric damage detection regardless of network timing
     */
    checkDistanceBasedDamage(): void {
        if (this.player.isDead() || this.player.isStunned) return;

        const fartChildren = this.farts.getChildren() as BasicFart[];

        for (const fart of fartChildren) {
            if (!fart.active) continue;

            // Skip our own farts
            const ownerId = fart.getData('ownerId');
            if (ownerId === networkManager.playerId) continue;

            // Already processed this fart
            if (fart.getData('damageProcessed')) continue;

            // Calculate distance using current visual scale
            const fartRadius = (fart.displayWidth / 2);
            const playerRadius = 14; // Approximate player radius (28/2)
            const distance = Phaser.Math.Distance.Between(
                this.player.x, this.player.y,
                fart.x, fart.y
            );

            // Check if within damage range
            if (distance < fartRadius + playerRadius) {
                // Check line-of-sight for non-piercing farts
                const chargePercent = fart.getData('chargePercent') ?? 0;
                if (chargePercent < 0.5 && !this.hasLineOfSight(fart.x, fart.y, this.player.x, this.player.y)) {
                    continue; // Wall blocks the damage
                }

                // Mark as processed to prevent duplicate damage
                fart.setData('damageProcessed', true);

                this.applyDamage(fart);
                fart.destroy();
                break; // Only process one fart per frame
            }
        }
    }

    /**
     * Apply damage from a fart to the player
     */
    private applyDamage(fart: BasicFart): void {
        if (this.player.isDead() || this.player.isStunned) return;

        const damage = fart.getDamage();
        const ownerId = fart.getData('ownerId') as string;

        this.player.takeDamage(damage);
        this.player.stun(fart.getStunDuration());

        // Record damage and check for kill
        if (ownerId) {
            this.statsTracker.recordDamage(ownerId, damage);
            if (this.player.health <= 0) {
                this.statsTracker.recordElimination(networkManager.playerId, ownerId);

                // Capture killing blow for replay
                this.deathReplaySystem.captureKillingBlow(
                    this.player.x, this.player.y,
                    fart.x, fart.y
                );
            }
        }

        if (this.isMultiplayer && this.multiplayerSync) {
            this.multiplayerSync.broadcastPosition();
        }
    }

    /**
     * Check if there's a clear line of sight between two points (no walls blocking)
     * Uses Bresenham's line algorithm
     */
    hasLineOfSight(x1: number, y1: number, x2: number, y2: number): boolean {
        const tileX1 = Math.floor(x1 / TILE_SIZE);
        const tileY1 = Math.floor(y1 / TILE_SIZE);
        const tileX2 = Math.floor(x2 / TILE_SIZE);
        const tileY2 = Math.floor(y2 / TILE_SIZE);

        // Bresenham's line algorithm
        const dx = Math.abs(tileX2 - tileX1);
        const dy = Math.abs(tileY2 - tileY1);
        const sx = tileX1 < tileX2 ? 1 : -1;
        const sy = tileY1 < tileY2 ? 1 : -1;
        let err = dx - dy;

        let x = tileX1;
        let y = tileY1;

        while (true) {
            // Check if current tile is a wall (skip start and end tiles)
            if ((x !== tileX1 || y !== tileY1) && (x !== tileX2 || y !== tileY2)) {
                if (y >= 0 && y < this.mazeData.length && x >= 0 && x < this.mazeData[0].length) {
                    if (this.mazeData[y][x] === TileType.WALL) {
                        return false; // Wall blocks line of sight
                    }
                }
            }

            if (x === tileX2 && y === tileY2) break;

            const e2 = 2 * err;
            if (e2 > -dy) {
                err -= dy;
                x += sx;
            }
            if (e2 < dx) {
                err += dx;
                y += sy;
            }
        }

        return true; // No walls blocking
    }
}
